import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";

import {
  BookingStatus,
  CompanyFilterTypes,
  RequestCustoms,
  RequestStatuses,
  RequestTypes,
  ShippingType,
  TableSortingOrder,
  UserType,
} from "@assets/constants";
import { useAppSelector, useDebouncedQueryParams } from "@assets/hooks";
import { FilteredRequests, Sort } from "@assets/requestsTypes";
import { WindowUtilities } from "@assets/utilities";
import CustomIcon from "@components/CustomIcon/customIcon";
import { faClock, faExternalLink, faUser } from "@fortawesome/free-solid-svg-icons";
import {
  CheckForm,
  Divider,
  FilterContext,
  Filters,
  FilterModal,
  FilterTags,
  Flex,
  Grid,
  Link,
  Page,
  Search,
  Section,
  Table,
  TableShortHandProps,
  Tag,
  Typography,
  PaginatedSortConfig,
  SortConfig,
} from "@samskip/frontend-components";
import RequestService from "@services/RequestService";
import { userState } from "@state/slices/userSlice";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import RequestCard from "./requestCard";

const Requests = () => {
  const { t } = useTranslation();
  const OldServiceWeb = process.env.OldServiceWeb;
  const defaultPageSize = 10;
  const defaultPageNumber = 1;
  const defaultOrder: string = TableSortingOrder.Ascending;
  const defaultActiveSorter = {
    dataIndex: "CreateTime",
    fieldId: "CreateTime",
  };

  const { filters, sections, changeFilter, getNormalizedFilters } = useContext(FilterContext);

  const [requests, setRequests] = useState<FilteredRequests[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState<CompanyFilterTypes>(
    CompanyFilterTypes.AllCompanies
  );
  const [isSelectedCompany, setIsSelectedCompany] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [pageNumber, setPageNumber] = useState<number>(defaultPageNumber);
  const [sort, setSort] = useState<Sort>({
    SortBy: defaultActiveSorter.fieldId,
    IsDesc: defaultOrder === "desc",
  });

  const queryParameters = useDebouncedQueryParams({
    filters,
    sort,
    searchInput,
    isSelectedCompany,
  });
  const suppressQueryRef = useRef(false);

  const userProfile = useAppSelector(userState).userProfile;
  const isUserCustomer = userProfile?.Access.UserType === UserType.CUSTOMER;

  const getStatusTag = (request: FilteredRequests): ReactNode => {
    switch (request.Status) {
      case RequestStatuses.PreRegistered:
        return (
          <Tag
            color="neutral-300"
            onClick={
              !isUserCustomer
                ? () =>
                    WindowUtilities.openTabOrWindow(`
           ${OldServiceWeb}/DeliveryModule.aspx?BookNos=${request.JobReference}&PreRequestId=${request.RequestNumber}&RequestType=${RequestTypes.Delivery}`)
                : undefined
            }
            size="small"
          >
            {t("LABEL_PREREGISTERED")}
          </Tag>
        );
      case RequestStatuses.Registered:
        return (
          <Tag color="neutral-300" size="small">
            {t("LABEL_REGISTERED")}
          </Tag>
        );
      case RequestStatuses.OnHold:
        return (
          <Tag color="warning" isLight size="small">
            {t("LABEL_ON_HOLD")}
          </Tag>
        );
      case RequestStatuses.InProgress:
        return (
          <Tag color="primary" size="small">
            {t("LABEL_IN_PROGRESS")}
          </Tag>
        );
      case RequestStatuses.Completed:
        return (
          <Tag color="success" isLight size="small">
            {t("LABEL_COMPLETED")}
          </Tag>
        );
      case RequestStatuses.Canceled:
        return (
          <Tag color="error" isLight size="small">
            {t("LABEL_CANCELED")}
          </Tag>
        );
      case RequestStatuses.Created:
        return (
          <Tag color="success" size="small">
            {t("LABEL_CREATED")}
          </Tag>
        );
      default:
        return (
          <Tag color="neutral-300" isLight size="small">
            {t("LABEL_INVALID_STATUS")}
          </Tag>
        );
    }
  };

  const getType = (type: string) => {
    switch (type) {
      case RequestTypes.BookingCorrection:
        return t("LABEL_BOOKING_CORRECTION");
      case RequestTypes.GeneralInquiry:
        return t("LABEL_GENERAL_INQUIRY");
      case RequestTypes.CustomsClearance:
        return t("LABEL_CUSTOMSCLEARED");
      case RequestTypes.ExportReportAssistance:
        return t(`LABEL_EXPISRPASSIST`);
      case RequestTypes.Delivery:
      case RequestTypes.OrderDelivery:
      case RequestTypes.AirDelivery:
      case RequestTypes.DomesticDrive:
        return t(`REQUESTTYPE_${type}`);
      default:
        return t("LABEL_N/A");
    }
  };

  const getBookingStatus = (bookingStatus: number) => {
    switch (bookingStatus) {
      case BookingStatus.BOOKED:
        return t("BOOKINGSTATUS_BOOKED");
      case BookingStatus.COLLECTED:
      case BookingStatus.PICKUP_SCHEDULED:
      case BookingStatus.PICKUP_REGISTERED:
        return t("BOOKINGSTATUS_COLLECTED");
      case BookingStatus.IN_TRANSIT:
        return t("BOOKINGSTATUS_IN_TRANSIT");
      case BookingStatus.DISCHARGED:
      case BookingStatus.SHIPMENT_UNLOADED:
        return t("BOOKINGSTATUS_DISCHARGED");
      case BookingStatus.IN_WAREHOUSE_RECEPTION:
        return t("BOOKINGSTATUS_IN_WAREHOUSE_RECEPTION");
      case BookingStatus.ARRIVED_IN_POD:
        return t("BOOKINGSTATUS_ARRIVED_IN_POD");
      case BookingStatus.BEING_PICKED_AT_WAREHOUSE:
        return t("BOOKINGSTATUS_BEING_PICKED_AT_WAREHOUSE");
      case BookingStatus.READY_FOR_DELIVERY:
        return t("BOOKINGSTATUS_READY_FOR_DELIVERY");
      case BookingStatus.LOADED_ON_TRUCK:
        return t("BOOKINGSTATUS_LOADED_ON_TRUCK");
      case BookingStatus.LOAD_ON_BOARD:
        return t("BOOKINGSTATUS_LOAD_ON_BOARD");
      case BookingStatus.DELIVERY_BOOKED:
        return t("BOOKINGSTATUS_DELIVERY_BOOKED");
      case BookingStatus.OUT_FOR_DELIVERY:
        return t("BOOKINGSTATUS_OUT_FOR_DELIVERY");
      case BookingStatus.DELIVERED:
      case BookingStatus.DELIVERED_POD:
      case BookingStatus.DELIVERED_TO_DOMESTIC_TRANSPORT:
      case BookingStatus.DELIVERED_TO_WAREHOUSE:
      case BookingStatus.PICKED_UP_BY_CUSTOMER:
      case BookingStatus.DELIVERED_EXPORT:
        return t("BOOKINGSTATUS_DELIVERED");
      default:
        return t("LABEL_N/A");
    }
  };

  const getCustoms = (custom: string) => {
    if (custom === RequestCustoms.Cleared) {
      return (
        <Typography>
          <CustomIcon
            className="mx-auto inline-block stroke-current text-green-800"
            iconName="customsDuty"
          />
          {t("LABEL_CLEARED")}
        </Typography>
      );
    }
    return (
      <Typography>
        <CustomIcon
          className="mx-auto inline-block stroke-current text-red-800"
          iconName="dutyFree"
        />
        {t("LABEL_AWAITING")}
      </Typography>
    );
  };

  const requestsColumns: TableShortHandProps<FilteredRequests>["columns"] = [
    {
      className: "min-w-32 whitespace-nowrap",
      dataIndex: "Status",
      id: "Status",
      title: t("LABEL_STATUS"),
      sortable: true,
      render: (request) => getStatusTag(request),
    },
    {
      className: "min-w-28",
      dataIndex: "RequestNumber",
      id: "RequestNumber",
      title: t("LABEL_REQUEST"),
      sortable: true,
      render: (request) =>
        request.RequestNumber ? (
          <Link
            href={`
           ${OldServiceWeb}/DisplayBookingInquiry.aspx?ReqNo=${request.RequestNumber}`}
            target="_blank"
          >
            {request.RequestNumber}
          </Link>
        ) : (
          t("LABEL_N/A")
        ),
    },
    {
      className: "min-w-36",
      dataIndex: "Type",
      id: "Type",
      title: t("LABEL_TYPE"),
      sortable: true,
      render: (request) => getType(request.Type ?? ""),
    },
    {
      className: "min-w-32",
      dataIndex: "JobReference",
      id: "JobReference",
      title: t("LABEL_BOOKING_NO"),
      sortable: true,
      render: (request) =>
        request.JobReference ? (
          <Link
            href={`/singleBooking/${request.FreightMode}/${request.JobReference}`}
            icon={faExternalLink}
            target="_blank"
          >
            {request.JobReference}
          </Link>
        ) : (
          t("LABEL_N/A")
        ),
    },
    {
      className: "min-w-24",
      dataIndex: "TypeOfShipment",
      id: "TypeOfShipment",
      title: t("LABEL_TYPE_OF_SHIPMENT"),
      sortable: true,
      render: (request) => (
        <Typography>
          {request.TypeOfShipment === "L" ? ShippingType.LCL : ShippingType.FCL ?? t("LABEL_N/A")}
        </Typography>
      ),
    },
    {
      className: "min-w-36",
      dataIndex: "BookingStatus",
      id: "BookingStatus",
      title: t("LABEL_BOOKING_STATUS"),
      sortable: true,
      render: (request) => getBookingStatus(parseInt(request.BookingStatus ?? "")),
    },
    {
      className: "min-w-32",
      dataIndex: "Customs",
      id: "Customs",
      title: t("LABEL_CUSTOMS"),
      sortable: true,
      render: (request) => getCustoms(request.Customs ?? ""),
    },
    {
      className: "min-w-40",
      dataIndex: "CustomerName",
      id: "CustomerName",
      title: t("LABEL_CUSTOMER"),
      sortable: true,
      render: (request) => <Typography>{request.CustomerName ?? t("LABEL_N/A")}</Typography>,
    },
    {
      className: "min-w-40",
      dataIndex: "UserName",
      id: "UserName",
      title: t("LABEL_USER"),
      sortable: true,
      render: (request) => (
        <Typography icon={faUser} iconColor="neutral-600">
          {request.UserName ?? t("LABEL_N/A")}
        </Typography>
      ),
    },
    {
      className: "min-w-36 whitespace-nowrap",
      dataIndex: "CreateTime",
      id: "CreateTime",
      title: t("LABEL_CREATED"),
      sortable: true,
      render: (request) => (
        <Typography icon={faClock} iconColor="neutral-600">
          {request.CreateTime
            ? format(new Date(request.CreateTime), "dd.MM.yyyy, h:mm").toString()
            : t("LABEL_N/A")}
        </Typography>
      ),
    },
    {
      className: "min-w-36 whitespace-nowrap",
      dataIndex: "CloseTime",
      id: "CloseTime",
      title: t("LABEL_CLOSED"),
      sortable: true,
      render: (request) => (
        <Typography icon={faClock} iconColor="neutral-600">
          {request.CloseTime
            ? format(new Date(request.CloseTime), "dd.MM.yyyy, h:mm").toString()
            : t("LABEL_N/A")}
        </Typography>
      ),
    },
  ];

  const fetchData = () => {
    setIsLoading(true);
    RequestService.getRequestsWithFilters({
      pageNumber: pageNumber,
      pageSize: pageSize,
      sort: sort,
      isSelectedCompany: isSelectedCompany,
      filters: {
        ...getNormalizedFilters(filters, sections),
        SearchString: searchInput ?? "",
      },
    })
      .then((requests) => {
        setRequests(requests);
      })
      .catch(() => toast.error("Something went wrong"))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!suppressQueryRef.current) {
      fetchData();
    }
  }, [queryParameters, pageNumber, pageSize]);

  const resetPage = () => {
    setPageNumber(defaultPageNumber);
  };

  const handleCardsFilterStatusChange = (value: string) => {
    let updatedCardsFilterStatus: (string | null)[];

    if (!filters.statuses.includes(value)) {
      updatedCardsFilterStatus = [...filters.statuses, value];
    } else {
      const index = filters.statuses.indexOf(value);
      updatedCardsFilterStatus = [
        ...filters.statuses.slice(0, index),
        ...filters.statuses.slice(index + 1),
      ];
    }

    changeFilter({ ...filters, statuses: updatedCardsFilterStatus });
    resetPage();
  };

  const handleCompanyStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyFilter(
      e.target.value === CompanyFilterTypes.SelectedCompany
        ? CompanyFilterTypes.SelectedCompany
        : CompanyFilterTypes.AllCompanies
    );
    setIsSelectedCompany(e.target.value === CompanyFilterTypes.SelectedCompany);
    resetPage();
  };

  const handleSearchStringChange = (value: string | null) => {
    setSearchInput(value);
    resetPage();
  };

  const handleFiltersChange = (newFilters: Filters) => {
    suppressQueryRef.current = true;
    changeFilter(newFilters);
    resetPage();
    setTimeout(() => {
      suppressQueryRef.current = false;
    }, 0);
  };

  const handleGetRequests = (paginatedSortConfig: PaginatedSortConfig<FilteredRequests>) => {
    setPageSize(paginatedSortConfig.pageSize);
    setPageNumber(paginatedSortConfig.page);
    setSort({
      SortBy: paginatedSortConfig.activeSorter?.fieldId ?? sort.SortBy,
      IsDesc: paginatedSortConfig.sortOrder === "desc",
    });
  };

  const sortConfig: SortConfig<FilteredRequests> = {
    onBackendSort: (paginatedSortConfig: PaginatedSortConfig<FilteredRequests>) =>
      handleGetRequests(paginatedSortConfig),
  };

  return (
    <Page
      size="large"
      actions={
        <FilterModal
          debounceTime={2000}
          onChange={(filters) => {
            handleFiltersChange(filters);
          }}
          title={t("LABEL_FILTERS")}
          triggerLabel={t("LABEL_FILTERS")}
        />
      }
      className="my-10"
      subtitle={
        <Flex gap="small">
          <CheckForm
            checked={companyFilter === CompanyFilterTypes.SelectedCompany}
            id="selected-company"
            label={t("LABEL_SELECTED_COMPANY")}
            name="company"
            onChange={handleCompanyStatusChange}
            type="radio"
            value={CompanyFilterTypes.SelectedCompany}
          />
          <CheckForm
            checked={companyFilter === CompanyFilterTypes.AllCompanies}
            id="my-companies"
            label={t("LABEL_ALL_COMPANIES")}
            name="company"
            onChange={handleCompanyStatusChange}
            type="radio"
            value={CompanyFilterTypes.AllCompanies}
          />
        </Flex>
      }
      title={t("LABEL_REQUESTS")}
    >
      <Flex gap={10} vertical>
        <Section>
          <Grid mdCol={4}>
            <RequestCard
              handleCardsFilterStatusChange={() =>
                handleCardsFilterStatusChange(RequestStatuses.PreRegistered)
              }
              isSelected={filters.statuses.includes(RequestStatuses.PreRegistered)}
              label={t("LABEL_PREREGISTERED")}
              loading={isLoading}
              value={requests[0]?.PreRegisteredCount?.toString() ?? "0"}
            />
            <RequestCard
              handleCardsFilterStatusChange={() =>
                handleCardsFilterStatusChange(RequestStatuses.Registered)
              }
              isSelected={filters.statuses.includes(RequestStatuses.Registered)}
              label={t("LABEL_REGISTERED")}
              loading={isLoading}
              value={requests[0]?.RegisteredCount?.toString() ?? "0"}
            />
            <RequestCard
              handleCardsFilterStatusChange={() =>
                handleCardsFilterStatusChange(RequestStatuses.OnHold)
              }
              isSelected={filters.statuses.includes(RequestStatuses.OnHold)}
              label={t("LABEL_ON_HOLD")}
              loading={isLoading}
              value={requests[0]?.OnHoldCount?.toString() ?? "0"}
            />
            <RequestCard
              handleCardsFilterStatusChange={() =>
                handleCardsFilterStatusChange(RequestStatuses.InProgress)
              }
              isSelected={filters.statuses.includes(RequestStatuses.InProgress)}
              label={t("LABEL_IN_PROGRESS")}
              loading={isLoading}
              value={requests[0]?.InProgressCount?.toString() ?? "0"}
            />
          </Grid>
        </Section>
        <Divider />
        <Section>
          <Grid mdCol={4}>
            <Search
              debounceTime={1000}
              fullWidth
              id="search-requests"
              onChange={handleSearchStringChange}
              onClear={() => handleSearchStringChange("")}
              placeholder={t("PLACEHOLDER_SEARCH_REQUESTS")}
              value={searchInput ?? ""}
            />
          </Grid>
          <FilterTags
            activeMessage={t("LABEL_FILTERS")}
            clearFiltersMessage={t("TEXT_CLEAR_ALL_FILTERS")}
            emptyMessage={t("TEXT_NO_FILTERS_APPLIED")}
            onChange={(filters) => {
              handleFiltersChange(filters);
            }}
          />
          <Table
            columns={requestsColumns}
            emptyStateMessage={{
              title: t("TEXT_NO_REQUESTS_FOUND"),
              description: t("TEXT_ADJUST_FILTERS_AND_RETRY"),
            }}
            getResultsMessage={(_page, _pageSize, totalCount) =>
              t("TEXT_N_REQUESTS_FOUND", { count: totalCount })
            }
            getRowId={(r) => r.RequestNumber}
            id="table-paginated-sorting"
            isLoading={isLoading}
            isStriped
            paginationConfig={{
              defaultPage: defaultPageNumber,
              defaultPageSize: defaultPageSize,
              totalCount: requests[0]?.TotalRequestCount ?? 0,
              onPaginationChange: handleGetRequests,
              pageSizeSelection: t("TEXT_RESULTS_PER_PAGE"),
            }}
            rows={requests}
            scrollable
            sortConfig={sortConfig}
          />
        </Section>
      </Flex>
    </Page>
  );
};

export default Requests;
